import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MagnifyingGlass, Money, Trophy, Lightning, TShirt, Image, EnvelopeSimple, ArrowsClockwise, Volleyball, UserCircle, CreditCard, Calendar, SortAscending, SortDescending } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'all', label: 'Todos', icon: Money },
  { id: 'card', label: 'Cartao', icon: CreditCard },
  { id: 'Mensalidade', label: 'Mensalidades', icon: Money },
  { id: 'Matricula', label: 'Matriculas', icon: UserCircle },
  { id: 'TK League', label: 'TK League', icon: Trophy },
  { id: 'Uniforme Liga', label: 'Unif. Liga', icon: Trophy },
  { id: 'Produtos', label: 'Produtos', icon: TShirt },
  { id: 'Aula Avulsa', label: 'Avulsa', icon: Lightning },
  { id: 'Avulso Feminino', label: 'Avulso Fem', icon: Volleyball },
];

const typeColors = {
  'Mensalidade': '#2D6A4F',
  'Matricula': '#0077B6',
  'TK League': '#E85D04',
  'Uniforme Liga': '#9B2226',
  'Aula Avulsa': '#0077B6',
  'Avulso Feminino': '#6A4C93',
  'Produtos': '#9B2226',
};

export default function AllPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all'); // 'all' | 'YYYY-MM'
  const [sortMode, setSortMode] = useState('date'); // 'date' | 'name-asc' | 'name-desc'
  const [syncing, setSyncing] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pago' | 'pendente'

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/all-payments`, { withCredentials: true });
      setPayments(data);
    } catch (e) {
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/stripe/sync-pending`, {}, { withCredentials: true });
      toast.success(data.message || 'Sincronizado!');
      fetchData();
    } catch (e) {
      toast.error('Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleSendReminder = async (payment) => {
    setSendingReminder(payment.id);
    try {
      const { data } = await axios.post(`${API}/admin/send-reminder`, {
        payment_id: payment.id,
        source_collection: payment.source_collection
      }, { withCredentials: true });
      toast.success(data.message || 'Cobranca enviada!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar cobranca');
    } finally {
      setSendingReminder(null);
    }
  };

  const viewReceipt = (payment) => {
    const token = localStorage.getItem('access_token');
    const endpoints = {
      'payments': `portal/zelle-receipt/${payment.id}`,
      'league': `league/zelle-receipt/${payment.id}`,
      'drop_in': `drop-in/zelle-receipt/${payment.id}`,
      'drop_in_fem': `drop-in-fem/zelle-receipt/${payment.id}`,
      'orders': `orders/zelle-receipt/${payment.id}`,
      'league_uniforms': `league/uniform/zelle-receipt/${payment.id}`,
    };
    const path = endpoints[payment.source_collection];
    if (path) window.open(`${API}/${path}?auth=${token}`, '_blank');
  };

  const isCardPayment = (p) => {
    const m = (p.payment_method || '').toLowerCase();
    return p.status === 'pago' && (m.includes('cartao') || m.includes('card') || m.includes('stripe') || m.includes('assinatura'));
  };

  const paymentMonth = (p) => {
    // Prefer reference_month, then date (YYYY-MM-DD or ISO), else ''
    const ref = p.reference_month;
    if (ref && typeof ref === 'string' && /^\d{4}-\d{2}/.test(ref)) return ref.slice(0, 7);
    const d = p.date || p.payment_date;
    if (d && typeof d === 'string' && d.length >= 7) return d.slice(0, 7);
    return '';
  };

  const availableMonths = [...new Set(payments.map(paymentMonth).filter(Boolean))].sort().reverse();

  const labelForMonth = (mk) => {
    if (!mk) return '';
    const [y, m] = mk.split('-');
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${labels[parseInt(m, 10) - 1]}/${y.slice(2)}`;
  };

  const filtered = payments.filter(p => {
    const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase());
    let matchTab;
    if (activeTab === 'all') matchTab = true;
    else if (activeTab === 'card') matchTab = isCardPayment(p);
    else matchTab = p.type === activeTab;
    const matchMonth = monthFilter === 'all' || paymentMonth(p) === monthFilter;
    let matchStatus = true;
    if (statusFilter === 'pago') matchStatus = p.status === 'pago';
    else if (statusFilter === 'pendente') matchStatus = p.status !== 'pago';
    return matchSearch && matchTab && matchMonth && matchStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === 'name-asc') return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    if (sortMode === 'name-desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
    // date desc (default)
    return (b.date || '').localeCompare(a.date || '');
  });

  const getTabCount = (tabId) => {
    if (tabId === 'all') return payments.length;
    if (tabId === 'card') return payments.filter(isCardPayment).length;
    return payments.filter(p => p.type === tabId).length;
  };

  const getTabPaid = (tabId) => {
    let items;
    if (tabId === 'all') items = payments;
    else if (tabId === 'card') items = payments.filter(isCardPayment);
    else items = payments.filter(p => p.type === tabId);
    if (monthFilter !== 'all') items = items.filter(p => paymentMonth(p) === monthFilter);
    return items.filter(p => p.status === 'pago').reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="all-payments-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Todos os Pagamentos</h1>
            <p className="text-[#73706A] mt-1">Visao consolidada de todas as receitas</p>
          </div>
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-[#0077B6] hover:bg-[#03045E] text-white"
            data-testid="sync-stripe-btn"
          >
            {syncing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <ArrowsClockwise size={16} className="mr-2" />
            )}
            Sincronizar Stripe
          </Button>
        </div>

        {/* Month filter strip */}
        {availableMonths.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="month-filter-strip">
            <Calendar size={18} className="text-[#73706A] shrink-0" />
            <button
              onClick={() => setMonthFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${monthFilter === 'all' ? 'bg-[#1A1A1A] text-white' : 'bg-[#F5F2EB] text-[#73706A] hover:bg-[#E6E2D8]'}`}
              data-testid="month-filter-all"
            >
              Todos os meses
            </button>
            {availableMonths.map(mk => (
              <button
                key={mk}
                onClick={() => setMonthFilter(mk)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${monthFilter === mk ? 'bg-[#E85D04] text-white' : 'bg-[#F5F2EB] text-[#73706A] hover:bg-[#E6E2D8]'}`}
                data-testid={`month-filter-${mk}`}
              >
                {labelForMonth(mk)}
              </button>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-[#F5F2EB]">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const count = getTabCount(tab.id);
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="text-xs px-3 py-2" data-testid={`tab-${tab.id}`}>
                  <Icon size={14} className="mr-1" /> {tab.label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(() => {
              // Counts ignoring statusFilter so cards show real totals + active state
              const baseList = payments.filter(p => {
                const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase());
                let matchTab;
                if (activeTab === 'all') matchTab = true;
                else if (activeTab === 'card') matchTab = isCardPayment(p);
                else matchTab = p.type === activeTab;
                const matchMonth = monthFilter === 'all' || paymentMonth(p) === monthFilter;
                return matchSearch && matchTab && matchMonth;
              });
              const paidCount = baseList.filter(p => p.status === 'pago').length;
              const pendCount = baseList.filter(p => p.status !== 'pago').length;
              return (
                <>
                  <div className="stat-card">
                    <p className="text-sm text-[#73706A]">Receita ({activeTab === 'all' ? 'Todos' : activeTab})</p>
                    <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">
                      $ {getTabPaid(activeTab).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'pago' ? 'all' : 'pago')}
                    className={`stat-card text-left transition-colors hover:bg-green-50 ${
                      statusFilter === 'pago' ? 'ring-2 ring-[#2D6A4F] bg-green-50' : ''
                    }`}
                    data-testid="status-card-pago"
                  >
                    <p className="text-sm text-[#73706A]">
                      Pagos {statusFilter === 'pago' && <span className="text-[10px] text-[#2D6A4F]">(filtrando)</span>}
                    </p>
                    <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{paidCount}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'pendente' ? 'all' : 'pendente')}
                    className={`stat-card text-left transition-colors hover:bg-orange-50 ${
                      statusFilter === 'pendente' ? 'ring-2 ring-[#E85D04] bg-orange-50' : ''
                    }`}
                    data-testid="status-card-pendente"
                  >
                    <p className="text-sm text-[#73706A]">
                      Pendentes {statusFilter === 'pendente' && <span className="text-[10px] text-[#E85D04]">(filtrando)</span>}
                    </p>
                    <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{pendCount}</p>
                  </button>
                </>
              );
            })()}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
              <Input placeholder="Buscar por nome..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-payments" />
            </div>
            {statusFilter !== 'all' && (
              <button
                onClick={() => setStatusFilter('all')}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                  statusFilter === 'pago' ? 'border-[#2D6A4F] text-[#2D6A4F] hover:bg-green-50' : 'border-[#E85D04] text-[#E85D04] hover:bg-orange-50'
                }`}
                data-testid="clear-status-filter"
              >
                Mostrando: {statusFilter === 'pago' ? 'Pagos' : 'Pendentes'} - clique pra limpar
              </button>
            )}
            <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="sort-controls">
              <button
                onClick={() => setSortMode('date')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'date' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
                data-testid="sort-date"
              >
                Recentes
              </button>
              <button
                onClick={() => setSortMode('name-asc')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
                data-testid="sort-name-asc"
              >
                <SortAscending size={14} /> A-Z
              </button>
              <button
                onClick={() => setSortMode('name-desc')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
                data-testid="sort-name-desc"
              >
                <SortDescending size={14} /> Z-A
              </button>
            </div>
          </div>

          <div className="mt-4 bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : sorted.length === 0 ? (
              <div className="p-8 text-center text-[#73706A]">Nenhum pagamento encontrado</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#F5F2EB]">
                      <th className="table-header">Tipo</th>
                      <th className="table-header">Nome</th>
                      <th className="table-header">Descricao</th>
                      <th className="table-header">Valor</th>
                      <th className="table-header">Metodo</th>
                      <th className="table-header">Status</th>
                      <th className="table-header text-right">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((p, index) => {
                      const color = typeColors[p.type] || '#73706A';
                      return (
                        <tr key={`${p.source_collection}-${p.id}-${index}`} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`payment-row-${index}`}>
                          <td className="table-cell">
                            <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: color + '15', color }}>{p.type}</span>
                          </td>
                          <td className="table-cell font-medium text-[#1A1A1A]">{p.name}</td>
                          <td className="table-cell text-sm text-[#73706A]">{p.description}</td>
                          <td className="table-cell font-medium text-[#2D6A4F]">$ {(p.amount || 0).toFixed(2)}</td>
                          <td className="table-cell text-sm capitalize">{p.payment_method}</td>
                          <td className="table-cell">
                            <span className={`badge ${p.status === 'pago' ? 'badge-success' : 'badge-warning'}`}>{p.status}</span>
                          </td>
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-1">
                              {p.zelle_receipt_path && (
                                <Button variant="ghost" size="sm" onClick={() => viewReceipt(p)} className="text-[#0077B6] hover:bg-blue-50" title="Ver comprovante">
                                  <Image size={16} />
                                </Button>
                              )}
                              {p.status !== 'pago' && (
                                <Button variant="ghost" size="sm" onClick={() => handleSendReminder(p)} disabled={sendingReminder === p.id} className="text-[#E85D04] hover:bg-orange-50" title="Enviar cobranca">
                                  {sendingReminder === p.id ? <div className="w-4 h-4 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" /> : <EnvelopeSimple size={16} />}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </Layout>
  );
}
